/**
 * Convert typed request payload to FormData
 * Values should be strings, numbers, booleans, or arrays
 * Arrays are automatically converted to field[] notation
 *
 * @param payload - The request payload object
 * @returns FormData instance ready for API requests
 *
 * @example
 * ```typescript
 * const formData = createFormData<PapiRequestDamzMessageSend>({
 *   room_id: 'room123',
 *   msg: 'Hello world',
 *   chatters: ['user123', 'user456'],
 *   attachments: {a: attachment1, b: attachment2}
 * });
 * // Results in FormData with:
 * // - room_id: 'room123'
 * // - msg: 'Hello world'
 * // - chatters[]: 'user123'
 * // - chatters[]: 'user456'
 * // - attachments[a]: JSON.stringify(attachment1)
 * // - attachments[b]: JSON.stringify(attachment2)
 * ```
 */
export function createFormData<T extends Record<string, any>>(payload: T): FormData {
  const formData = new FormData();

  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    if (value instanceof File) {
      formData.append(key, value);
      return;
    }

    if (Array.isArray(value)) {
      value.forEach(item => {
        if (item !== undefined && item !== null) {
          formData.append(`${key}[]`, String(item));
        }
      });
      return;
    }

    if (typeof value === 'object') {
      Object.entries(value).forEach(([innerKey, innerValue]) => {
        formData.append(`${key}[${innerKey}]`, JSON.stringify(innerValue));
      });
      return;
    }

    formData.append(key, String(value));
  });

  return formData;
}
